import { ref, computed, inject, watch } from 'vue';
import { useRouter } from 'vue-router';
import { useDocument, useCurrentUser, useFirestore, useFirebaseAuth } from 'vuefire';
import { defineStore } from 'pinia';
import dayjs from 'dayjs';

import { collection, addDoc, setDoc, doc } from 'firebase/firestore';
import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signOut,
  sendEmailVerification,
  sendPasswordResetEmail,
  confirmPasswordReset as confirmPasswordResetAuth,
  updateProfile as updateAuthProfile,
  updateEmail,
  updatePassword,
  reauthenticateWithCredential,
  EmailAuthProvider,
  deleteUser
} from 'firebase/auth';

import { useApplicationStore } from '/src/stores/application';


export const useUserStore = defineStore('user', () => {
  const user = ref(null);
  const error = ref(null);
  const analytics = inject('analytics');
  const currentUserAuth = useCurrentUser();
  const db = useFirestore()
  const auth = useFirebaseAuth()

  watch(currentUserAuth, (newUser) => {
    if (newUser) {
      user.value = newUser;
      analytics.identify(newUser.uid, {
        email: newUser.email,
        name: newUser.displayName,
      });
      analytics.trackLogin();
    } else {
      user.value = null;
    }
  }, { immediate: true });

  const router = useRouter()

  const applicationStore = useApplicationStore();

  const isLoggedIn = computed(() => user.value !== null);
  
  async function login(email, password) {
    try {
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      user.value = userCredential.user;
      error.value = null;


      applicationStore.setCurrentReading(undefined);
      analytics.track('login')
      return user.value;
    } catch (e) {
      error.value = e;
      throw e;
    }
  }

  async function storeReading(userData, fullName, dateTimeOfBirth, placeOfBirth, timezone, isPrimaryReading = false) {
    try {
      const docRef = await addDoc(collection(db, 'users', userData.uid, 'readings'), {
        name: fullName,
        dateTimeOfBirth: dayjs(dateTimeOfBirth).format('YYYY-MM-DD HH:mm'),
        timezone,
        placeOfBirth,
        isPrimaryReading,
      });

      analytics.track('add_reading', {
        primary: isPrimaryReading,
      });

      applicationStore.setCurrentReading(docRef.id);

      return useDocument(docRef);
    } catch (e) {
      console.error('Error storing reading:', e);
      throw e;
    }
  }

  async function reauthenticate(userData, password) {
    try {
      const credential = EmailAuthProvider.credential(userData.email, password);
      await reauthenticateWithCredential(userData, credential);
    } catch (e) {
      console.error('Reauthentication failed:', e);
      throw e;
    }
  }

  async function updateProfile(userData, fullName, email, locale = 'de') {
    console.log(locale)
    try {
      await updateEmail(userData, email);
      await updateAuthProfile(userData, {
        displayName: fullName,
      });

      await setDoc(
        doc(db, 'users', userData.uid),
        { fullName, email, locale },
        { merge: true }
      );
    } catch (e) {
      console.error('Profile update failed:', e);
      throw e;
    }
  }

  async function register(email, password, fullName, dateTimeOfBirth, placeOfBirth, timeZone, locale) {
    try {
      const { user: newUser } = await createUserWithEmailAndPassword(auth, email, password);
      applicationStore.setCurrentReading(null);

      user.value = newUser;
      error.value = null;

      const utmParameters = applicationStore.utmParameters;
      const tags = utmParameters?.campaign 
        ? ['coisar_signup', `campaign_${utmParameters.campaign}`]
        : ['coisar_signup'];

      await updateAuthProfile(newUser, { displayName: fullName });
      
      await setDoc(doc(db, 'users', newUser.uid), {
        fullName,
        email,
        locale,
        marketing: {
          subscribe: true,
          tags,
          utmParameters
        }
      });

      analytics.trackSignup();

      return await storeReading(newUser, fullName, dateTimeOfBirth, placeOfBirth, timeZone, true);
    } catch (e) {
      console.error('Registration failed:', e);
      throw e;
    }
  }

  async function logout() {
    try {
      await signOut(auth);
      await applicationStore.setCurrentReading(undefined);
      user.value = null;
      router.push({ name: 'landing' })
    } catch (e) {
      console.error('Logout failed:', e);
      throw e;
    }
  }

  async function updatePassword(userData, newPassword) {
    try {
      await updatePassword(userData, newPassword);
    } catch (e) {
      console.error('Password update failed:', e);
      throw e;
    }
  }

  async function deleteUserAccount(userData) {
    try {
      await deleteUser(userData);
      user.value = null;
    } catch (e) {
      console.error('User deletion failed:', e);
      throw e;
    }
  }

  async function resetPassword(email) {
    try {
      await sendPasswordResetEmail(auth, email);
      return true;
    } catch (e) {
      error.value = e;
      console.error('Password reset failed:', e);
      throw e;
    }
  }

  async function confirmPasswordReset(password, code) {
    try {
      await confirmPasswordResetAuth(auth, code, password);
      return true;
    } catch (e) {
      error.value = e;
      console.error('Password reset confirmation failed:', e);
      throw e;
    }
  }

  return {
    // state
    user,
    error,
    
    // getters
    isLoggedIn,
    
    // actions
    login,
    storeReading,
    reauthenticate,
    updateProfile,
    register,
    logout,
    updatePassword,
    deleteUser: deleteUserAccount,
    resetPassword,
    confirmPasswordReset
  };
});
